// src/pages/FruitDetail.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ShoppingCart,
  WhatsApp,
  LocationOn,
  Scale,
  LocalShipping,
  Inventory,
  Grade,
  ArrowBack,
  Info,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { fruitsAPI, enquiryAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/helpers';

const FruitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [fruit, setFruit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiryDialogOpen, setEnquiryDialogOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchFruitDetail();
  }, [id]);

  const fetchFruitDetail = async () => {
    try {
      setLoading(true);
      const response = await fruitsAPI.getById(id);
      setFruit(response.data);
      setQuantity(response.data.moq || 1);
    } catch (error) {
      console.error('Error fetching fruit detail:', error);
      toast.error('Failed to load fruit details');
      navigate('/fruits');
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiry = () => {
    if (!user) {
      toast.error('Please login to make an enquiry');
      navigate('/login');
      return;
    }
    
    if (!user.verified && user.user_type === 'buyer') {
      toast.error('Please complete verification first');
      navigate('/buyer/verification');
      return;
    }
    
    setEnquiryDialogOpen(true);
  };

  const handleSubmitEnquiry = async () => {
    try {
      setSubmitting(true);
      const enquiryData = {
        fruit_id: parseInt(id),
        quantity: quantity,
        message: message,
      };
      
      const response = await enquiryAPI.create(enquiryData);
      
      toast.success('Enquiry submitted successfully!');
      setEnquiryDialogOpen(false);
      
      // Redirect to enquiry page or show success
      navigate(`/buyer/enquiries`);
    } catch (error) {
      console.error('Error creating enquiry:', error);
      const errorMsg = error.response?.data?.error || 'Failed to submit enquiry';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const fruitInfo = `Product: ${fruit.name} (${fruit.variety})
Origin: ${fruit.origin_country}
Size: ${fruit.size}
MOQ: ${fruit.moq} containers
Price: ${fruit.price_per_unit ? `$${fruit.price_per_unit}/unit` : 'Contact for price'}`;
    
    const message = `Hello, I'm interested in making an enquiry about:
    
${fruitInfo}

${message ? `Additional details: ${message}` : ''}`;
    
    const whatsappUrl = `https://wa.me/1234567890?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const steps = ['Select Quantity', 'Add Details', 'Review & Submit'];

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading fruit details...</Typography>
      </Container>
    );
  }

  if (!fruit) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Fruit not found</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/fruits')}
          sx={{ mt: 2 }}
        >
          Back to Fruits
        </Button>
      </Container>
    );
  }

  const imageUrl = fruit.image_url || `http://localhost:5000${fruit.image_url}` || '/default-fruit.jpg';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/fruits')}
        sx={{ mb: 3 }}
      >
        Back to Fruits
      </Button>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Left Column - Image */}
          <Grid item xs={12} md={5}>
            <Card>
              <CardMedia
                component="img"
                height="400"
                image={imageUrl}
                alt={fruit.name}
                sx={{ objectFit: 'cover' }}
              />
            </Card>
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<ShoppingCart />}
                onClick={handleEnquiry}
                disabled={!user}
              >
                {user ? 'Start Enquiry' : 'Login to Enquire'}
              </Button>
              
              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<WhatsApp />}
                onClick={handleWhatsApp}
              >
                Contact on WhatsApp
              </Button>
            </Box>
          </Grid>

          {/* Right Column - Details */}
          <Grid item xs={12} md={7}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Typography variant="h4" component="h1">
                  {fruit.name}
                </Typography>
                <Chip label={fruit.grade || 'Standard'} color="primary" />
              </Box>
              
              <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
                {fruit.price_per_unit ? formatCurrency(fruit.price_per_unit) + '/unit' : 'Price on request'}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" paragraph>
                {fruit.description || 'No description available.'}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Product Details Table */}
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info /> Product Details
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>
                      Variety
                    </TableCell>
                    <TableCell>{fruit.variety || 'N/A'}</TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOn fontSize="small" />
                        Origin Country
                      </Box>
                    </TableCell>
                    <TableCell>{fruit.origin_country || 'N/A'}</TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Scale fontSize="small" />
                        Size
                      </Box>
                    </TableCell>
                    <TableCell>{fruit.size || 'N/A'}</TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Inventory fontSize="small" />
                        Minimum Order Quantity (MOQ)
                      </Box>
                    </TableCell>
                    <TableCell>{fruit.moq || 1} containers</TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Grade fontSize="small" />
                        Grade
                      </Box>
                    </TableCell>
                    <TableCell>{fruit.grade || 'Standard'}</TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalShipping fontSize="small" />
                        Container Type
                      </Box>
                    </TableCell>
                    <TableCell>{fruit.container_type || 'Standard Container'}</TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Port of Destination
                    </TableCell>
                    <TableCell>{fruit.port_of_destination || 'N/A'}</TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Port of Discharge
                    </TableCell>
                    <TableCell>{fruit.port_of_discharge || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Additional Information */}
            <Alert severity="info" icon={<Info />}>
              <Typography variant="body2">
                • All prices are per unit in US dollars
                <br />
                • Minimum order quantity applies
                <br />
                • Shipping costs calculated separately
                <br />
                • Contact us for bulk orders and custom requirements
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </Paper>

      {/* Enquiry Dialog */}
      <Dialog 
        open={enquiryDialogOpen} 
        onClose={() => setEnquiryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Make Enquiry - {fruit.name}</DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Quantity
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Minimum Order Quantity: {fruit.moq} containers
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Quantity (containers)"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(fruit.moq, parseInt(e.target.value) || fruit.moq))}
                inputProps={{ min: fruit.moq }}
                sx={{ mb: 3 }}
              />
              
              {fruit.price_per_unit && (
                <Alert severity="info">
                  Estimated total: {formatCurrency(quantity * fruit.price_per_unit)}
                  <br />
                  <Typography variant="caption">
                    (Advance payment: {formatCurrency(quantity * fruit.price_per_unit * 0.3)})
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Additional Details
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Please provide any special requirements or additional information
              </Typography>
              <TextField
                fullWidth
                label="Message / Requirements"
                multiline
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Example: Need delivery by [date], special packaging requirements, quality specifications..."
              />
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review Enquiry
              </Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>Product</TableCell>
                      <TableCell>{fruit.name} ({fruit.variety})</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                      <TableCell>{quantity} containers</TableCell>
                    </TableRow>
                    {fruit.price_per_unit && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>Estimated Total</TableCell>
                        <TableCell>{formatCurrency(quantity * fruit.price_per_unit)}</TableCell>
                      </TableRow>
                    )}
                    {message && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>Requirements</TableCell>
                        <TableCell>{message}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Alert severity="info">
                After submission, our team will contact you within 24 hours to discuss details.
              </Alert>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(prev => prev - 1)}>
              Back
            </Button>
          )}
          
          {activeStep < steps.length - 1 ? (
            <Button 
              variant="contained" 
              onClick={() => setActiveStep(prev => prev + 1)}
            >
              Next
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleSubmitEnquiry}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Submitting...' : 'Submit Enquiry'}
            </Button>
          )}
          
          <Button onClick={() => setEnquiryDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FruitDetail;